import React, { useState } from "react";
import { addDoc, getDocs, collection, query, where } from "firebase/firestore";
import { db } from "../../Environment/firebase";
import {
  Typography,
  TextField,
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { departments} from "../../Environment/Config";

const DiseaseAdd = () => {
  const [diseaseData, setDiseaseData] = useState({
    name: "",
    caseList: [""],
    tag: "",
    furigana: "",
  });
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessages, setErrorMessages] = useState({
    name: "",
    furigana: "",
    tag: "",
    caseList: [""],
  });
  const navigate = useNavigate();

  const handleInputChange = (field: string, value: string) => {
    setDiseaseData((prevData) => ({
      ...prevData,
      [field]: value.slice(0, 300),
    }));
    if (field === "name" && value.trim() === "") {
      setErrorMessages((prevErrors) => ({
        ...prevErrors,
        name: "病名は必須です。",
      }));
    } else {
      setErrorMessages((prevErrors) => ({
        ...prevErrors,
        name: "",
      }));
    }
  };

  const handleCaseListChange = (index: number, value: string) => {
    setDiseaseData((prevData) => {
      const updatedCaseList = [...prevData.caseList];
      updatedCaseList[index] = value;
      return { ...prevData, caseList: updatedCaseList };
    });
    setErrorMessages((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      updatedErrors.caseList[index] = "";
      return updatedErrors;
    });
  };

  const addCaseField = () => {
    setDiseaseData((prevData) => ({
      ...prevData,
      caseList: [...prevData.caseList, ""],
    }));
    setErrorMessages((prevErrors) => ({
      ...prevErrors,
      caseList: [...prevErrors.caseList, ""],
    }));
  };

  const removeCaseField = (index: number) => {
    setDiseaseData((prevData) => {
      const updatedCaseList = [...prevData.caseList];
      updatedCaseList.splice(index, 1);
      return { ...prevData, caseList: updatedCaseList };
    });
    setErrorMessages((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      updatedErrors.caseList.splice(index, 1);
      return updatedErrors;
    });
  };

  const validateCaseList = async () => {
    const caseList = diseaseData.caseList;
    const errors = [...errorMessages.caseList];
    const promises = caseList.map(async (caseItem, index) => {
      if (!caseItem.trim()) {
        errors[index] = "このフィールドは空白にできません";
        return false;
      }
      const caseQuery = query(
        collection(db, "dwh"),
        where("share", "in", ["true", "mnist"]),
        where("id", "==", caseItem)
      );
      const querySnapshot = await getDocs(caseQuery);
      if (querySnapshot.empty) {
        errors[index] = "このIDは存在しません";
        return false;
      } else {
        errors[index] = "";
        return true;
      }
    });

    const results = await Promise.all(promises);
    setErrorMessages((prevErrors) => ({
      ...prevErrors,
      caseList: errors,
    }));
    return results.every((exists) => exists);
  };

  const saveDiseaseData = async () => {
    if (!diseaseData.name.trim()) {
      setErrorMessages((prevErrors) => ({
        ...prevErrors,
        name: "病名は必須です。",
      }));
      setMessage("必須項目を入力してください。");
      return;
    }
    if (!diseaseData.furigana.trim()){
      setErrorMessages((prevErrors) => ({
        ...prevErrors,
        furigana: "ふりがなは必須です。",
      }));
      setMessage("必須項目を入力してください。");
      return;
    }
    if (!diseaseData.tag.trim()){
      setErrorMessages((prevErrors) => ({
        ...prevErrors,
        tag: "診断科は必須です。",
      }));
      setMessage("必須項目を入力してください。");
      return;
    }

    const allCasesValid = await validateCaseList();
    if (!allCasesValid) {
      setMessage("典型例のエラーを修正してください。");
      return;
    }

    const nameQuery = query(
      collection(db, "search"),
      where("name", "==", diseaseData.name)
    );
    const nameQuerySnapshot = await getDocs(nameQuery);

    if (!nameQuerySnapshot.empty) {
      setErrorMessages((prevErrors) => ({
        ...prevErrors,
        name: "この疾患はすでに登録されています。",
      }));
      setMessage("この疾患はすでに登録されています。");
      setIsSaving(false);
      return;
    }

    setIsSaving(true);
    try {
      await addDoc(collection(db, "search"), {
        name: diseaseData.name,
        case_list: diseaseData.caseList,
        tag: diseaseData.tag,
        furigana: diseaseData.furigana,
      });
      setMessage("病名が正常に追加されました。");
      navigate("/manage/catalogue/disease");
    } catch (error) {
      console.error("データの保存エラー:", error);
      setMessage("データの保存中にエラーが発生しました。");
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <Grid container justifyContent="center">
      
      <Grid
        item
        xs={12}
        sm={7}
        mx={3}
        sx={{ mt: 3, minHeight: "95vh", textAlign: "center" }}
      >
        {message && (
        <Typography variant="body1" color="error" sx={{ mt: 2 }}>
          {message}
        </Typography>
      )}
        <TextField
          label="病名"
          variant="outlined"
          fullWidth
          required
          value={diseaseData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          sx={{ my: 5 }}
          error={Boolean(errorMessages.name)}
          helperText={errorMessages.name}
        />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl fullWidth required>
              <TextField
                variant="outlined"
                label="ふりがな"
                required
                value={diseaseData.furigana}
                onChange={(e) => handleInputChange("furigana", e.target.value)}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth required>
              <InputLabel id="tag-label">タグ</InputLabel>
              <Select
                labelId="tag-label"
                value={diseaseData.tag}
                onChange={(e) => handleInputChange("tag", e.target.value)}
                label="タグ"
              >
                {departments.map((tag) => (
                  <MenuItem key={tag} value={tag}>
                    {tag}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Typography variant="h6" sx={{ mt: 2 }}>
          典型例
        </Typography>
        <Grid item>
          {diseaseData.caseList.map((caseItem, index) => (
            <Grid
              item
              key={index}
              sx={{ display: "flex", alignItems: "center", mb: 2 }}
            >
              <TextField
                label={`症例 ${index + 1}`}
                placeholder="典型例のurlの最後のパスを入力してください"
                variant="outlined"
                fullWidth
                value={caseItem}
                onChange={(e) => handleCaseListChange(index, e.target.value)}
                error={Boolean(errorMessages.caseList[index])}
                helperText={errorMessages.caseList[index]}
              />
              {diseaseData.caseList.length > 1 && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => removeCaseField(index)}
                      sx={{ ml: 1 }}
                    >
                      削除
                    </Button>
                  )}
            </Grid>
          ))}
          <Grid item display="flex" justifyContent="start">
            <Button variant="outlined" color="primary" onClick={addCaseField}>
              追加
            </Button>
          </Grid>
        </Grid>

        <Button
          variant="contained"
          color="primary"
          onClick={saveDiseaseData}
          disabled={isSaving}
          sx={{ mt: 3 }}
        >
          保存
        </Button>
      </Grid>
    </Grid>
  );
};

export default DiseaseAdd;
